import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, MessageCircleHeart, Bug, Lightbulb, Heart, MessageSquare,
    ChevronDown, Check, Send, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const TYPE_META = {
    idea: { label: 'Idea', icon: Lightbulb, tone: 'bg-amber-100 text-amber-800' },
    bug: { label: 'Bug', icon: Bug, tone: 'bg-red-100 text-red-800' },
    praise: { label: 'Praise', icon: Heart, tone: 'bg-emerald-100 text-emerald-800' },
    other: { label: 'Other', icon: MessageSquare, tone: 'bg-zinc-100 text-zinc-700' },
};

const STATUS_META = {
    new: { label: 'New', tone: 'bg-emerald-100 text-emerald-800' },
    in_progress: { label: 'In progress', tone: 'bg-amber-100 text-amber-800' },
    resolved: { label: 'Resolved', tone: 'bg-zinc-100 text-zinc-600' },
};

const FILTERS = [
    { key: null, label: 'All' },
    { key: 'new', label: 'New' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'resolved', label: 'Resolved' },
];

const relTime = (iso) => {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
};

// FeedbackList - admin panel view listing every feedback submission.
// Rows expand inline to show the full message + attached context, an
// admin-note field, and status toggles. The user's email in each row
// links to `/admin/users/:userId` for the full profile.
export const FeedbackList = ({ initialStatus = null }) => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [typeFilter, setTypeFilter] = useState(null);
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0 });
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '100' });
            if (statusFilter) params.set('status', statusFilter);
            if (typeFilter) params.set('type', typeFilter);
            const res = await fetch(`${API}/admin/feedback?${params}`, { credentials: 'include' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setItems(data.items || []);
            setCounts(data.counts || counts);
        } catch (err) {
            toast.error(err.message || 'Failed to load feedback');
        } finally {
            setLoading(false);
        }
    // counts is intentionally omitted from deps — we always want the fresh values from the response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, typeFilter]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const updateItem = async (id, patch) => {
        try {
            const res = await fetch(`${API}/admin/feedback/${id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // Optimistic local update — refetch for canonical counts.
            setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
            fetchList();
        } catch (err) {
            toast.error(err.message || 'Update failed');
        }
    };

    // Send a reply email via the backend. Local row is patched with the
    // new reply so it appears in the thread instantly; a refetch keeps
    // counters + status transitions in sync.
    const sendReply = async (id, message) => {
        const res = await fetch(`${API}/admin/feedback/${id}/reply`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Send failed (${res.status})`);
        }
        const data = await res.json();
        toast.success(`Reply sent to ${data.sent_to || 'user'}`);
        fetchList();
    };

    // Fire-and-forget mark-read call when the admin expands a row.
    // Optimistically clears the local flag so the badge count drops
    // instantly; a background refetch reconciles server truth.
    const markRead = async (id) => {
        setItems(prev => prev.map(i => i.id === id ? { ...i, admin_unread: false } : i));
        try {
            await fetch(`${API}/admin/feedback/${id}/mark-read`, {
                method: 'POST',
                credentials: 'include',
            });
            fetchList();
        } catch (_) { /* silent - the optimistic update stays */ }
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-feedback-list">
            {/* Header + filter chips */}
            <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <MessageCircleHeart className="w-4 h-4" />
                        Feedback ({counts.total})
                    </h2>
                    <span className="text-xs text-muted-foreground">{counts.new} new &middot; {counts.in_progress} in progress</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {FILTERS.map((f) => (
                        <button
                            key={f.label}
                            onClick={() => setStatusFilter(f.key)}
                            className={
                                'px-2.5 py-1 text-[11.5px] rounded-full border transition-colors ' +
                                (statusFilter === f.key
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-background text-muted-foreground border-border hover:bg-secondary')
                            }
                            data-testid={`feedback-filter-${f.key || 'all'}`}
                        >
                            {f.label}
                            {f.key && counts[f.key] > 0 && (
                                <span className="ml-1.5 text-[10px] opacity-70">{counts[f.key]}</span>
                            )}
                        </button>
                    ))}
                    <span className="mx-1 text-muted-foreground">|</span>
                    {Object.entries(TYPE_META).map(([key, meta]) => {
                        const Icon = meta.icon;
                        const active = typeFilter === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setTypeFilter(active ? null : key)}
                                className={
                                    'inline-flex items-center gap-1 px-2.5 py-1 text-[11.5px] rounded-full border transition-colors ' +
                                    (active
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:bg-secondary')
                                }
                                data-testid={`feedback-type-filter-${key}`}
                            >
                                <Icon className="w-3 h-3" />
                                {meta.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
            ) : items.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                    No feedback matching these filters.
                </div>
            ) : (
                <ul className="divide-y divide-border" data-testid="admin-feedback-rows">
                    {items.map((fb) => {
                        const typeMeta = TYPE_META[fb.type] || TYPE_META.other;
                        const statusMeta = STATUS_META[fb.status] || STATUS_META.new;
                        const TypeIcon = typeMeta.icon;
                        const isOpen = expanded === fb.id;
                        return (
                            <li key={fb.id} data-testid={`feedback-row-${fb.id}`}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nextOpen = isOpen ? null : fb.id;
                                        setExpanded(nextOpen);
                                        // Opening the row acknowledges the latest
                                        // state - clears the unread flag so the
                                        // badge count drops.
                                        if (nextOpen && fb.admin_unread) markRead(fb.id);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors text-left"
                                >
                                    {/* Small red dot at the leading edge indicates
                                        an unread row - lets the admin scan the
                                        list for what needs attention at a glance. */}
                                    <span
                                        aria-hidden={!fb.admin_unread}
                                        className={
                                            'flex-shrink-0 w-2 h-2 rounded-full ' +
                                            (fb.admin_unread ? 'bg-rose-500' : 'bg-transparent')
                                        }
                                        data-testid={fb.admin_unread ? 'feedback-unread-dot' : undefined}
                                    />
                                    <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${typeMeta.tone}`}>
                                        <TypeIcon className="w-3 h-3" />
                                        {typeMeta.label}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-foreground truncate">{fb.message}</p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${fb.user_id}`); }}
                                                className="hover:text-foreground hover:underline"
                                                data-testid="feedback-user-link"
                                            >
                                                {fb.email || fb.user_id}
                                            </button>
                                            {' '}&middot; {relTime(fb.created_at)}
                                            {' '}&middot; {fb.context?.app_surface || 'web'}
                                        </p>
                                    </div>
                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusMeta.tone}`}>
                                        {statusMeta.label}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isOpen && (
                                    <div className="px-4 pb-4 pt-1 bg-secondary/20 border-t border-border/40 space-y-3">
                                        {/* Full message + context */}
                                        <blockquote className="text-sm leading-relaxed whitespace-pre-wrap bg-white rounded-lg p-3 border border-border">
                                            {fb.message}
                                        </blockquote>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                                            <ContextField label="Page" value={fb.context?.page} />
                                            <ContextField label="Surface" value={fb.context?.app_surface || 'web'} />
                                            <ContextField label="Viewport" value={fb.context?.viewport} />
                                            <ContextField label="Tier" value={fb.tier} />
                                        </div>
                                        {fb.context?.user_agent && (
                                            <ContextField label="User agent" value={fb.context.user_agent} full />
                                        )}
                                        {fb.context?.console_errors?.length > 0 && (
                                            <details className="text-[11px]">
                                                <summary className="cursor-pointer text-muted-foreground">
                                                    {fb.context.console_errors.length} recent console error(s)
                                                </summary>
                                                <pre className="mt-2 p-2 bg-zinc-900 text-zinc-100 text-[10.5px] rounded overflow-x-auto whitespace-pre-wrap">
                                                    {fb.context.console_errors.map(e => `[${e.ts}] ${e.level}: ${e.message}`).join('\n')}
                                                </pre>
                                            </details>
                                        )}
                                        {/* Admin note */}
                                        <AdminNote fb={fb} onSave={(note) => updateItem(fb.id, { admin_note: note })} />
                                        {/* Actions */}
                                        <div className="flex flex-wrap items-center gap-2 pt-1">
                                            <button
                                                onClick={() => updateItem(fb.id, { status: 'in_progress' })}
                                                disabled={fb.status === 'in_progress'}
                                                className="px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-amber-100 text-amber-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-200"
                                                data-testid="feedback-mark-in-progress"
                                            >
                                                Mark in progress
                                            </button>
                                            <button
                                                onClick={() => updateItem(fb.id, { status: 'resolved' })}
                                                disabled={fb.status === 'resolved'}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-emerald-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700"
                                                data-testid="feedback-mark-resolved"
                                            >
                                                <Check className="w-3 h-3" />
                                                Resolve
                                            </button>
                                            {fb.status !== 'new' && (
                                                <button
                                                    onClick={() => updateItem(fb.id, { status: 'new' })}
                                                    className="px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-secondary text-muted-foreground hover:bg-secondary/80"
                                                    data-testid="feedback-reopen"
                                                >
                                                    Reopen
                                                </button>
                                            )}
                                        </div>
                                        {/* Reply thread + inline composer. Every reply
                                            already sent to the user is rendered above
                                            the composer so the admin sees the full
                                            conversation without leaving the row. */}
                                        <ReplyThread fb={fb} onSend={(msg) => sendReply(fb.id, msg)} />
                                        {fb.resolved_by && (
                                            <p className="text-[11px] text-muted-foreground">
                                                Resolved by {fb.resolved_by} &middot; {relTime(fb.resolved_at)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const ContextField = ({ label, value, full }) => (
    <div className={full ? 'col-span-full' : ''}>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-[11.5px] font-medium text-foreground break-all">{value || '—'}</p>
    </div>
);

const AdminNote = ({ fb, onSave }) => {
    const [note, setNote] = useState(fb.admin_note || '');
    const [saving, setSaving] = useState(false);
    const dirty = note !== (fb.admin_note || '');
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Private admin note</p>
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Add a note — visible only in the admin panel"
                className="w-full text-[12.5px] p-2 rounded-lg border border-border bg-white resize-none"
                data-testid="feedback-admin-note"
            />
            {dirty && (
                <button
                    onClick={async () => { setSaving(true); await onSave(note); setSaving(false); }}
                    disabled={saving}
                    className="mt-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                    data-testid="feedback-admin-note-save"
                >
                    {saving ? 'Saving...' : 'Save note'}
                </button>
            )}
        </div>
    );
};

// ReplyThread - renders the reply history for a feedback row and hosts
// the inline composer for sending a new reply. The composer emails the
// user via /api/admin/feedback/{id}/reply on the backend, which handles
// the from/reply-to swap and appends the message to the row.
const ReplyThread = ({ fb, onSend }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [error, setError] = useState('');
    const replies = Array.isArray(fb.replies) ? fb.replies : [];

    // Ask the backend for an AI draft. Whatever is already in the box is
    // sent as a steer, so "tell her yes but next month" becomes a full
    // reply instead of being thrown away.
    const draft = async () => {
        if (drafting || sending) return;
        setError('');
        setDrafting(true);
        try {
            const res = await fetch(`${API}/admin/feedback/${fb.id}/draft-reply`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ hint: message.trim() || null }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Could not draft a reply');
            setMessage(data.draft || '');
        } catch (err) {
            setError(err.message || 'Could not draft a reply');
        } finally {
            setDrafting(false);
        }
    };

    const submit = async () => {
        const trimmed = message.trim();
        if (!trimmed || sending) return;
        setError('');
        setSending(true);
        try {
            await onSend(trimmed);
            setMessage('');
        } catch (err) {
            setError(err.message || 'Send failed');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="pt-2" data-testid="feedback-reply-thread">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Reply to {fb.email}</p>
            {replies.length > 0 && (
                <ul className="space-y-2 mb-2">
                    {replies.map((r, idx) => {
                        // Two visual treatments so a scan of the thread
                        // makes the direction obvious at a glance:
                        //   - `outbound` (default / no direction): white
                        //     card, aligned left. What the admin sent.
                        //   - `inbound`: emerald-tinted card with a
                        //     "User reply" chip, so incoming messages
                        //     from the user pop visually in the admin UI.
                        const inbound = r.direction === 'inbound';
                        return (
                            <li
                                key={r.id || idx}
                                className={
                                    'rounded-lg p-2.5 border ' +
                                    (inbound
                                        ? 'border-emerald-200 bg-emerald-50/60'
                                        : 'border-border bg-white')
                                }
                                data-testid={inbound ? 'feedback-reply-inbound' : 'feedback-reply-item'}
                            >
                                <p className="text-[11px] text-muted-foreground mb-1 flex items-center gap-1.5">
                                    {inbound && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9.5px] font-semibold uppercase tracking-wider">
                                            User reply
                                        </span>
                                    )}
                                    <strong className="text-foreground">{r.from || (inbound ? 'user' : 'support')}</strong>
                                    <span>&middot;</span>
                                    <span>{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</span>
                                </p>
                                <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap text-foreground">{r.message}</p>
                            </li>
                        );
                    })}
                </ul>
            )}
            <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder={`Write your reply. Sent from support@brainsync.pro — the user's reply will land in the same inbox.`}
                className="w-full text-[12.5px] p-2 rounded-lg border border-border bg-white resize-none"
                data-testid="feedback-reply-textarea"
                disabled={sending || drafting}
            />
            {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
            <div className="flex items-center justify-between mt-1.5 gap-2">
                <p className="text-[10.5px] text-muted-foreground">
                    Original quoted automatically. Sending status auto-bumps to <em>In progress</em>.
                </p>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={draft}
                        disabled={drafting || sending}
                        title={message.trim()
                            ? 'Rewrite what you started into a full reply'
                            : 'Draft a reply based on their message'}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="feedback-reply-draft"
                    >
                        {drafting
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                        {drafting ? 'Drafting...' : 'Draft reply'}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!message.trim() || sending || drafting}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="feedback-reply-send"
                    >
                        <Send className="w-3 h-3" />
                        {sending ? 'Sending...' : 'Send reply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeedbackList;
